import {
  GR_NODE_TYPES,
  MAX_CHILDREN,
  MAX_DEPTH,
} from '@sonifi/wb-guest-request/constants/wbGuestRequestConstants';
import { PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';
import {
  buildPlainNodesfromMenu,
  findNodeParent,
  isNodeRoot,
} from '@sonifi/wb-tree/utils/helper';

export const findNodesByPlatform = ({ menu, node, platform }) => {
  const nodeParent = findNodeParent({ menu, node });
  const nodeIsRoot = isNodeRoot({
    menu,
    nodeId: node.id,
  });
  const parentIsRoot = nodeParent
    ? isNodeRoot({ menu, nodeId: nodeParent.id })
    : false;
  const nodePhonePreviewSelected =
    nodeIsRoot || parentIsRoot ? node : nodeParent;
  const nodeTvPreviewSelected = nodeIsRoot
    ? node.children[0]
    : parentIsRoot
    ? node
    : nodeParent;
  const nodePreviewSelected =
    platform === PLATFORMS.tv
      ? nodeTvPreviewSelected
      : nodePhonePreviewSelected;

  return { nodeParent, nodePreviewSelected };
};

export const findNodePreviewHCTV = (node) => {
  if (
    node.type === GR_NODE_TYPES.serviceCategories &&
    node.valid_child_types?.includes(GR_NODE_TYPES.serviceCategories)
  ) {
    return { nodeFound: node, hctvCategoryPreview: true };
  }

  if (
    node.type === GR_NODE_TYPES.serviceCategories &&
    node.valid_child_types?.includes(GR_NODE_TYPES.serviceIssues)
  ) {
    return node.children?.length === 1
      ? { nodeFound: node.children[0], hctvCategoryPreview: false }
      : { nodeFound: node, hctvCategoryPreview: true };
  }
  return { nodeFound: node, hctvCategoryPreview: false };
};

const childrenFilterActive = (children) => {
  children.forEach((ch, index) => {
    if (ch.children?.length) {
      childrenFilterActive(ch.children);
    }
    if (!ch.active) {
      delete children[index];
    }
  });
  return children.filter((ch) => ch.active);
};

export const createTvVisibleChildren = ({ menu, nodeSelected }) => {
  const children = childrenFilterActive(
    JSON.parse(JSON.stringify(menu.children)),
  );
  const currentIndex = children.findIndex((ch) => ch.id === nodeSelected.id);

  if (currentIndex > -1) {
    const tempArr = [];
    if (children && children.length > 4) {
      for (let i = currentIndex; i < children.length; i++) {
        tempArr.push(children[i]);

        if (tempArr.length === 3) {
          break;
        }
      }

      if (tempArr.length < 3) {
        let i = 0;
        while (tempArr.length < 3) {
          tempArr.push(children[i]);
          i++;
        }
      }

      if (currentIndex > 1 && currentIndex + 2 < children.length) {
        for (let i = currentIndex - 2; i < currentIndex; i++) {
          if (children[i]) {
            tempArr.push(children[i]);
          }
        }
      } else if (currentIndex > 1) {
        tempArr.push(children[currentIndex - 2]);
        tempArr.push(children[currentIndex - 1]);
      } else if (currentIndex === 0) {
        tempArr.push(children[children.length - 2]);
        tempArr.push(children[children.length - 1]);
      } else if (currentIndex === 1) {
        tempArr.push(children[children.length - 1]);
        tempArr.push(children[0]);
      }

      const visibleChildren = [];
      visibleChildren.push(tempArr[3]);
      visibleChildren.push(tempArr[4]);
      visibleChildren.push(tempArr[0]);
      visibleChildren.push(tempArr[1]);
      visibleChildren.push(tempArr[2]);
      return visibleChildren;
    } else {
      for (let i = currentIndex; i < children.length; i++) {
        tempArr.push(children[i]);
      }

      for (let i = 0; i < currentIndex; i++) {
        if (children[i]) {
          tempArr.push(children[i]);
        }
      }
      let visibleChildren = [];
      if (tempArr.length === 0) {
        visibleChildren = [null, null, null, null, null];
      } else if (tempArr.length === 1) {
        visibleChildren = [null, null, tempArr[0], null, null];
      } else if (tempArr.length === 2) {
        visibleChildren = [null, null, tempArr[0], tempArr[1], null];
      } else if (tempArr.length === 3) {
        visibleChildren = [null, tempArr[2], tempArr[0], tempArr[1], null];
      } else if (tempArr.length === 4) {
        visibleChildren = [
          null,
          tempArr[3],
          tempArr[0],
          tempArr[1],
          tempArr[2],
        ];
      }

      return visibleChildren;
    }
  }
};

export const createCategoryChildren = ({
  selectedNodeChildren,
  menuSelectedNodeIndex,
}) => {
  if (selectedNodeChildren.length > 9) {
    if (menuSelectedNodeIndex < 9) {
      return [...selectedNodeChildren].slice(0, 9);
    } else {
      return [...selectedNodeChildren].slice(
        menuSelectedNodeIndex - 8,
        menuSelectedNodeIndex + 1,
      );
    }
  }
  return [...selectedNodeChildren];
};

export const showEditIcon = (item) => {
  if (
    !item.valid_child_types ||
    (item.valid_child_types &&
      item.valid_child_types[0] !== GR_NODE_TYPES.serviceCategories)
  ) {
    return true;
  }
  return false;
};

export const showRemove = (item) => {
  if (
    !item.valid_child_types ||
    (item.valid_child_types &&
      item.valid_child_types[0] !== GR_NODE_TYPES.serviceCategories)
  ) {
    return true;
  }
  return false;
};

const doesNodeHaveUnpublishedChanges = (nodeSubject, lastPublished) => {
  const subjectDate = new Date(nodeSubject.last_updated);
  const lastP = lastPublished ? (typeof lastPublished === 'string' ? new Date(lastPublished) : lastPublished) : null;
  return nodeSubject.isZombie || subjectDate > lastP;
};

const doesNodeHaveChildrenWithUnpublishedChanges = (node, lastPublished) => {
  let hasError = false;
  const plainMenuNodes = buildPlainNodesfromMenu({
    menu: node,
    init: true,
  });
  for (const ch of plainMenuNodes) {
    if (doesNodeHaveUnpublishedChanges(ch.subject, lastPublished)) {
      hasError = true;
    }
  }
  return hasError;
};

export const validate = ({ node, lastPublished }) => {
  if (node?.subject?.hasDeletedContent) {
    return 'References deleted content';
  } else if (doesNodeHaveUnpublishedChanges(node?.subject, lastPublished)) {
    return 'Has unpublished changes';
  } else if (doesNodeHaveChildrenWithUnpublishedChanges(node, lastPublished)) {
    return 'Has children with unpublished changes';
  }
};

export const isBranch = (node) =>
  (node.subject.isZombie && !node.children?.length) ||
  node.type === GR_NODE_TYPES.serviceCategories;

export const getHasUnpublishedChanges = ({
  serviceMenu,
  lastPublished,
  productSettings,
  serviceCenterSettings,
}) => new Date(serviceMenu.last_updated) > lastPublished ||
  new Date(productSettings.last_updated) > lastPublished ||
  new Date(serviceCenterSettings.last_updated) > lastPublished ||
  doesNodeHaveChildrenWithUnpublishedChanges(serviceMenu.menu, lastPublished);

export const getPublishStatusMessage = ({
  lastPublishJob,
  isIncompleteJob,
  isFailedJob,
}) => {
  if (isFailedJob) {
    return 'Failed to publish on:';
  }
  if (isIncompleteJob) {
    return 'Publish request submitted on:';
  }
  if (!lastPublishJob) {
    return 'Not yet published';
  }
  return 'Last Published:';
};

export const findAndUpdateNodes = ({ children, nodes, lastUpdated }) => {
  const childrenRes = children.map((ch) => {
    if (ch.children) {
      return {
        ...ch,
        children: findAndUpdateNodes({ children: ch.children, nodes }),
        subject: {
          ...ch.subject,
          last_updated: nodes.some((node) => node.id === ch.id)
            ? lastUpdated
            : ch.subject.last_updated,
        },
      };
    } else {
      return {
        ...ch,
        subject: {
          ...ch.subject,
          last_updated: nodes.some((node) => node.id === ch.id)
            ? lastUpdated
            : ch.subject.last_updated,
        },
      };
    }
  });
  return childrenRes;
};

const isBeyondMaxDepth = (depth) => depth > MAX_DEPTH;

export const isBeyondMaxChildren = (childrenCount) =>
  childrenCount >= MAX_CHILDREN;

export const getAddTooltip = (node, depth) => {
  if (isBeyondMaxDepth(depth)) {
    return {
      title:
        'You cannot add items to this menu item as it is beyond the maximum depth of this tree',
      disabled: true,
    };
  }

  if (isBeyondMaxChildren(node.children?.length || 0)) {
    return {
      title: `You cannot add more than ${MAX_CHILDREN} items to this tree`,
      disabled: true,
    };
  }

  return {
    title: 'Add',
    disabled: false,
  };
};
